import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { useGoBack } from "@/hooks/useGoBack";
import { cn } from "@/lib/utils";
import { shellBackFallback, shouldShowShellBack } from "./backNav";

/**
 * The shell-level Back arrow, at the left edge of the top bar. Renders nothing
 * on tabs and on pages that draw their own Back (see backNav.ts). Uses
 * useGoBack, so it still goes somewhere when the page was opened cold from a
 * link — navigate(-1) alone is a no-op on a tab's first entry.
 */
export function ShellBackButton({
  isAuthenticated,
  className,
}: {
  isAuthenticated: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const goBack = useGoBack(shellBackFallback(isAuthenticated));

  if (!shouldShowShellBack(pathname)) return null;

  return (
    <button
      type="button"
      onClick={goBack}
      aria-label={t("common.back")}
      className={cn(
        "shrink-0 rounded-full p-1.5 text-text-tertiary hover:bg-surface-tertiary hover:text-text-secondary sm:p-2",
        className,
      )}
    >
      <ArrowLeft className="h-5 w-5" />
    </button>
  );
}
